/**
 * Material node utilities for contenteditable editor
 * These functions create DOM elements for material nodes
 */

import { i18n } from '@/i18n';

// ==================== 颜色工具函数 ====================

/**
 * 检测字符串是否是有效的颜色值
 * 支持: #RGB, #RRGGBB, #RRGGBBAA, rgb(), rgba(), hsl(), hsla(), 颜色名称
 * @param {string} str - 要检测的字符串
 * @returns {boolean}
 */
export const isValidColor = (str) => {
  if (!str || typeof str !== 'string') return false;
  const trimmed = str.trim();

  // HEX 颜色 (#RGB, #RRGGBB, #RRGGBBAA)
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed)) {
    return true;
  }

  // rgb/rgba
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*(0|1|0?\.\d+))?\s*\)$/i.test(trimmed)) {
    return true;
  }

  // hsl/hsla
  if (/^hsla?\(\s*\d+\s*,\s*\d+%?\s*,\s*\d+%?\s*(,\s*(0|1|0?\.\d+))?\s*\)$/i.test(trimmed)) {
    return true;
  }

  // CSS 颜色名称（常用）
  const namedColors = [
    'black',
    'white',
    'red',
    'green',
    'blue',
    'yellow',
    'orange',
    'purple',
    'pink',
    'gray',
    'grey',
    'brown',
    'cyan',
    'magenta',
    'lime',
    'maroon',
    'navy',
    'olive',
    'teal',
    'aqua',
    'fuchsia',
    'silver',
    'gold',
    'coral',
    'salmon',
    'tomato',
    'crimson',
    'indigo',
    'violet',
    'plum',
    'orchid',
    'lavender',
    'beige',
    'ivory',
    'khaki',
    'tan',
    'chocolate',
    'sienna',
    'peru',
    'wheat',
    'moccasin',
    'peachpuff',
    'bisque',
    'blanchedalmond',
    'papayawhip',
    'lemonchiffon',
    'lightgoldenrodyellow',
    'lightyellow',
    'cornsilk',
    'floralwhite',
    'seashell',
    'snow',
    'honeydew',
    'mintcream',
    'azure',
    'aliceblue',
    'ghostwhite',
    'whitesmoke',
    'gainsboro',
    'lightgray',
    'lightgrey',
    'darkgray',
    'darkgrey',
    'dimgray',
    'dimgrey',
    'slategray',
    'slategrey',
    'lightslategray',
    'lightslategrey',
    'transparent',
  ];
  if (namedColors.includes(trimmed.toLowerCase())) {
    return true;
  }

  return false;
};

/**
 * 规范化颜色值为 HEX 格式（用于显示）
 * @param {string} color - 颜色值
 * @returns {string} 规范化的 HEX 颜色或原值
 */
export const normalizeColorToHex = (color) => {
  if (!color) return color;

  // 如果已经是 HEX 格式，直接返回（可能需要补全）
  if (color.startsWith('#')) {
    if (color.length === 4) {
      // #RGB -> #RRGGBB
      return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
    }
    return color.toUpperCase();
  }

  // 使用 canvas 来转换颜色
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = color;
    const computedColor = ctx.fillStyle;
    // fillStyle 会返回 #rrggbb 格式
    if (computedColor.startsWith('#')) {
      return computedColor.toUpperCase();
    }
    // 如果返回 rgb() 格式，转换为 HEX
    const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
      const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
      const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`.toUpperCase();
    }
  } catch {
    // 转换失败，返回原值
  }
  return color;
};

/**
 * 获取颜色的亮度（用于判断是否使用深色或浅色文字）
 * @param {string} color - HEX 颜色值
 * @returns {number} 0-255 的亮度值
 */
export const getColorLuminance = (color) => {
  if (!color) return 128;

  let hex = color;
  if (hex.startsWith('#')) {
    hex = hex.slice(1);
  }

  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // 使用感知亮度公式
  return (r * 299 + g * 587 + b * 114) / 1000;
};

/**
 * 创建颜色 Tag 元素 - Apple/Linear 风格
 * 简洁设计，无编辑/删除按钮，通过正常文本编辑操作即可删除
 * @param {Object} colorData - 颜色数据 { id, value, displayValue }
 * @param {Object} options - 配置选项
 * @param {Function} options.makeDraggable - 使元素可拖拽的函数
 * @param {Function} options.onClick - 点击回调函数 (colorData, element, event) => void
 * @returns {HTMLElement} 颜色 Tag 元素
 */
export const createColorTagElement = (colorData, options = {}) => {
  const { makeDraggable, onClick } = options;

  const normalizedColor = normalizeColorToHex(colorData.value);
  const luminance = getColorLuminance(normalizedColor);
  // 浅色阈值：亮度 > 200 视为非常浅，需要特殊处理
  const isVeryLight = luminance > 200;
  const isLight = luminance > 160;

  const container = document.createElement('span');
  container.contentEditable = 'false';
  container.setAttribute('data-type', 'color');
  container.setAttribute('data-color-id', colorData.id);
  container.setAttribute('data-value', colorData.value);
  container.setAttribute('data-display-value', colorData.displayValue || normalizedColor);

  // Apple/Linear 风格：精致、简洁、高对比度
  container.className =
    'color-tag inline-flex items-center gap-1 mx-0.5 px-1.5 py-px rounded select-none align-baseline';

  // 根据亮度调整样式
  // 浅色：使用灰色文字 + 更明显的边框
  // 深色：使用颜色本身作为文字色
  const textColor = isVeryLight ? '#666666' : isLight ? '#444444' : normalizedColor;
  const bgOpacity = isVeryLight ? 0.4 : isLight ? 0.2 : 0.15;
  const borderOpacity = isVeryLight ? '60' : isLight ? '40' : '30';

  container.style.cssText = `
    background-color: ${normalizedColor}${Math.round(bgOpacity * 255)
      .toString(16)
      .padStart(2, '0')};
    color: ${textColor};
    font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace;
    font-size: 0.8125em;
    font-weight: 500;
    letter-spacing: -0.01em;
    line-height: 1.4;
    border: 1px solid ${normalizedColor}${borderOpacity};
    transition: all 0.15s ease;
    cursor: pointer;
  `;

  if (makeDraggable) {
    makeDraggable(container);
  }

  // Add click handler for editing
  if (onClick) {
    container.addEventListener('click', (e) => {
      // Prevent click during drag
      if (container.classList.contains('dragging')) return;
      e.preventDefault();
      e.stopPropagation();
      onClick(
        {
          id: colorData.id,
          value: colorData.value,
          displayValue: colorData.displayValue || normalizedColor,
        },
        container,
        e
      );
    });
  }

  // 颜色色块 - 小圆角方块，浅色增加边框可见性
  const colorSwatch = document.createElement('span');
  colorSwatch.className = 'flex-shrink-0';
  const swatchShadow = isVeryLight
    ? 'inset 0 0 0 1px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.08)'
    : 'inset 0 0 0 1px rgba(0,0,0,0.1)';
  colorSwatch.style.cssText = `
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background-color: ${normalizedColor};
    box-shadow: ${swatchShadow};
  `;

  // 颜色值文字
  const valueText = document.createElement('span');
  valueText.className = 'pointer-events-none';
  valueText.textContent = normalizedColor;

  container.appendChild(colorSwatch);
  container.appendChild(valueText);

  return container;
};

/**
 * 更新颜色 Tag 节点 - 适配新的简洁结构
 * @param {HTMLElement} editorRef - 编辑器元素
 * @param {string} colorId - 颜色 ID
 * @param {Object} newColorData - 新的颜色数据
 * @param {Function} onSync - 同步内容回调
 */
export const updateColorTagNode = (editorRef, colorId, newColorData, onSync) => {
  if (!editorRef) return;
  const node = editorRef.querySelector(`[data-color-id="${colorId}"]`);
  if (node) {
    const normalized = normalizeColorToHex(newColorData.value);
    const luminance = getColorLuminance(normalized);
    const isVeryLight = luminance > 200;
    const isLight = luminance > 160;

    const textColor = isVeryLight ? '#666666' : isLight ? '#444444' : normalized;
    const bgOpacity = isVeryLight ? 0.4 : isLight ? 0.2 : 0.15;
    const borderOpacity = isVeryLight ? '60' : isLight ? '40' : '30';

    node.setAttribute('data-value', newColorData.value);
    node.setAttribute('data-display-value', normalized);
    node.style.backgroundColor = `${normalized}${Math.round(bgOpacity * 255)
      .toString(16)
      .padStart(2, '0')}`;
    node.style.color = textColor;
    node.style.borderColor = `${normalized}${borderOpacity}`;

    // 更新色块
    const swatch = node.querySelector('span:first-child');
    if (swatch) {
      swatch.style.backgroundColor = normalized;
      const swatchShadow = isVeryLight
        ? 'inset 0 0 0 1px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.08)'
        : 'inset 0 0 0 1px rgba(0,0,0,0.1)';
      swatch.style.boxShadow = swatchShadow;
    }
    // 更新文字
    const text = node.querySelector('span:last-child');
    if (text) {
      text.textContent = normalized;
    }

    onSync?.();
  }
};

/**
 * 通过 ID 移除颜色 Tag 节点
 * @param {HTMLElement} editorRef - 编辑器元素
 * @param {string} colorId - 颜色 ID
 * @param {Function} onSync - 同步内容回调
 */
export const removeColorTagNodeById = (editorRef, colorId, onSync) => {
  if (!editorRef) return;
  const node = editorRef.querySelector(`[data-color-id="${colorId}"]`);
  if (node) {
    node.remove();
    onSync?.();
  }
};

// 素材用途选项（用于图片素材的用途标签）- 使用 i18n key
export const materialUsageKeys = [
  { id: 'background', labelKey: 'material.background' },
  { id: 'product', labelKey: 'material.product' },
  { id: 'model', labelKey: 'material.model' },
  { id: 'logo', labelKey: 'material.logo' },
  { id: 'qrcode', labelKey: 'material.qrcode' },
  { id: 'icon', labelKey: 'material.icon' },
  { id: 'decoration', labelKey: 'material.decoration' },
  { id: 'texture', labelKey: 'material.texture' },
  { id: 'chart', labelKey: 'material.chart' },
];

/**
 * 获取翻译后的素材用途列表
 * @returns {Array} 带有翻译后 label 的用途列表
 */
export const getMaterialUsages = () => {
  return materialUsageKeys.map((usage) => ({
    id: usage.id,
    label: i18n.t(usage.labelKey),
  }));
};

// 编辑器标签选项（用于 # 触发的标签菜单）- 使用 i18n key
export const editorTagKeys = [
  { id: 'product', labelKey: 'material.product', color: 'blue' },
  { id: 'brand', labelKey: 'editor.brand', color: 'purple' },
  { id: 'slogan', labelKey: 'editor.slogan', color: 'green' },
  { id: 'title', labelKey: 'editor.title', color: 'orange' },
  { id: 'description', labelKey: 'editor.description', color: 'pink' },
  { id: 'cta', labelKey: 'editor.cta', color: 'red' },
  { id: 'logo', labelKey: 'material.logo', color: 'indigo' },
  { id: 'background', labelKey: 'material.background', color: 'teal' },
];

/**
 * 获取翻译后的编辑器标签列表
 * @returns {Array} 带有翻译后 label 的标签列表
 */
export const getEditorTags = () => {
  return editorTagKeys.map((tag) => ({
    id: tag.id,
    label: i18n.t(tag.labelKey),
    color: tag.color,
  }));
};

/**
 * 根据查询过滤编辑器标签
 * @param {string} query - 搜索查询
 * @returns {Array} 过滤后的标签列表
 */
export const filterEditorTags = (query) => {
  const tags = getEditorTags();
  if (!query) return tags;
  const lowerQuery = query.toLowerCase();
  return tags.filter(
    (tag) =>
      tag.label.toLowerCase().includes(lowerQuery) || tag.id.toLowerCase().includes(lowerQuery)
  );
};

/**
 * 格式化单个素材为 @SeedeMaterial 格式
 * @param {Object} asset - 素材对象
 * @returns {string} 格式化后的字符串
 */
export const formatMaterialPlaceholder = (asset) => {
  if (!asset) return '';
  const w = asset.meta?.w || asset.width || 128;
  const h = asset.meta?.h || asset.height || 128;
  const safeFilename = (asset.name || '').replace(/[\r\n]+/g, ' ');
  return `@SeedeMaterial(${JSON.stringify({
    id: asset.id || '',
    filename: safeFilename,
    url: asset.url,
    width: w,
    height: h,
    aspectRatio: Math.round((100 * w) / h) / 100,
    tag: asset.tag || '',
  })})`;
};

/**
 * 更新用途标签显示
 * @param {HTMLElement} container - 素材容器元素
 * @param {string} tag - 用途标签
 */
export const updateUsageLabel = (container, tag) => {
  const label = container.querySelector('.usage-trigger');
  if (label) {
    // 更新文本
    const labelText = label.querySelector('span');
    if (labelText) {
      labelText.textContent = tag || i18n.t('material.clickToSetUsage');
    }
    // 更新样式
    label.className = `usage-trigger absolute bottom-1.5 left-1.5 right-1.5 flex items-center justify-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-all duration-200 cursor-pointer z-10 ${
      tag
        ? 'bg-neutral-900/90 text-white backdrop-blur-sm shadow-lg'
        : 'bg-black/50 text-white/90 backdrop-blur-sm opacity-0 group-hover:opacity-100'
    }`;
  }
};

/**
 * 创建用途选择弹窗
 * @param {HTMLElement} container - 素材容器元素
 * @param {Object} asset - 素材对象
 * @param {Object} options - 配置选项
 * @param {Function} options.onTagChange - 标签变更回调
 * @param {Function} options.onSync - 同步内容回调
 * @returns {HTMLElement} 弹窗元素
 */
export const createUsagePopover = (container, asset, options = {}) => {
  const { onTagChange, onSync } = options;

  const popover = document.createElement('div');
  popover.className =
    'usage-popover absolute left-0 bottom-full mb-2 w-64 p-3 bg-white/95 backdrop-blur-xl rounded-xl shadow-xl border border-black/5 z-50 hidden';

  // 标题
  const header = document.createElement('div');
  header.className = 'text-xs font-medium text-black/70 mb-2';
  header.textContent = i18n.t('material.selectUsage');
  popover.appendChild(header);

  // 用途网格 - 使用翻译后的标签
  const grid = document.createElement('div');
  grid.className = 'grid grid-cols-3 gap-1.5 mb-2';

  const usages = getMaterialUsages();
  usages.forEach((usage) => {
    const btn = document.createElement('button');
    const isSelected = asset.tag === usage.label;
    btn.className = `px-2 py-1.5 text-xs rounded-lg transition-all ${
      isSelected
        ? 'bg-gradient-to-br from-[#20f6da]/20 to-[#86EEBA]/20 text-black/80 border border-[#20f6da]/30'
        : 'bg-black/5 text-black/60 hover:bg-black/10 border border-transparent'
    }`;
    btn.textContent = usage.label;
    btn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      asset.tag = usage.label;
      container.setAttribute('data-tag', usage.label);
      updateUsageLabel(container, usage.label);
      onTagChange?.(asset, usage.label);
      popover.classList.add('hidden');
      onSync?.();
    };
    grid.appendChild(btn);
  });
  popover.appendChild(grid);

  // 自定义输入
  const customRow = document.createElement('div');
  customRow.className = 'flex gap-1.5';
  const customInput = document.createElement('input');
  customInput.type = 'text';
  customInput.placeholder = i18n.t('material.customUsagePlaceholder');
  customInput.className =
    'flex-1 text-xs px-2 py-1.5 border border-black/10 rounded-lg bg-white/60 focus:outline-none focus:border-[#20f6da]/40';
  customInput.onclick = (e) => e.stopPropagation();

  const customBtn = document.createElement('button');
  customBtn.className =
    'px-2 py-1.5 text-xs bg-gradient-to-r from-[#20f6da] to-[#86EEBA] text-white rounded-lg hover:shadow-md transition-all';
  customBtn.textContent = i18n.t('material.add');
  customBtn.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const value = customInput.value.trim();
    if (value) {
      asset.tag = value;
      container.setAttribute('data-tag', value);
      updateUsageLabel(container, value);
      onTagChange?.(asset, value);
      popover.classList.add('hidden');
      onSync?.();
    }
  };
  customRow.appendChild(customInput);
  customRow.appendChild(customBtn);
  popover.appendChild(customRow);

  return popover;
};

// 默认预览尺寸限制
const DEFAULT_PREVIEW_SIZE = 120;
const MAX_PREVIEW_SIZE = 220;
const MIN_PREVIEW_WIDTH = 120;
const MIN_PREVIEW_HEIGHT = 72;
const MAX_RESIZE_SIZE = 400;

/**
 * 创建 4 角 resize handles
 * @param {HTMLElement} imgWrapper - 图片包装器
 * @param {HTMLElement} container - 容器元素
 * @param {Object} asset - 素材对象
 * @param {Function} onSync - 同步回调
 * @returns {HTMLElement} handles 容器
 */
const createResizeHandles = (imgWrapper, container, asset, onSync) => {
  const handlesContainer = document.createElement('div');
  handlesContainer.className = 'resize-handles';
  handlesContainer.style.cssText = `
        position: absolute;
        inset: 0;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
    `;

  // 4 个角的配置 - 更紧凑的设计
  const corners = [
    { position: 'nw', cursor: 'nwse-resize', style: 'top: -3px; left: -3px;' },
    { position: 'ne', cursor: 'nesw-resize', style: 'top: -3px; right: -3px;' },
    { position: 'sw', cursor: 'nesw-resize', style: 'bottom: -3px; left: -3px;' },
    { position: 'se', cursor: 'nwse-resize', style: 'bottom: -3px; right: -3px;' },
  ];

  // 获取图片宽高比
  const getAspectRatio = () => {
    const img = imgWrapper.querySelector('img');
    if (img && img.naturalWidth && img.naturalHeight) {
      return img.naturalWidth / img.naturalHeight;
    }
    return (asset.width || 1) / (asset.height || 1);
  };

  corners.forEach(({ position, cursor, style }) => {
    const handle = document.createElement('div');
    handle.className = `resize-handle-${position}`;
    handle.style.cssText = `
            position: absolute;
            width: 12px;
            height: 12px;
            border-radius: 7px;
            background: rgb(255, 255, 255);
            border: 2px solid rgb(23, 23, 23);
            box-shadow: rgba(0, 0, 0, 0.2) 0px 1px 3px;
            pointer-events: auto;
            transition: 0.15s;
            ${style}
            cursor: ${cursor};
            z-index: 20;
            transform: scale(1);
        `;
    handle.onmouseenter = () => {
      handle.style.transform = 'scale(1.2)';
    };
    handle.onmouseleave = () => {
      handle.style.transform = 'scale(1)';
    };

    let isResizing = false;
    let startX = 0;
    let startY = 0;
    let startWidth = 0;

    handle.onmousedown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      isResizing = true;
      startX = e.clientX;
      startY = e.clientY;
      startWidth = imgWrapper.offsetWidth;
      container.setAttribute('draggable', 'false');

      // 添加全局 resize 样式
      document.body.style.cursor = cursor;
      document.body.style.userSelect = 'none';

      const aspectRatio = getAspectRatio();

      const onMouseMove = (e) => {
        if (!isResizing) return;

        let deltaX = e.clientX - startX;
        let deltaY = e.clientY - startY;

        // 根据角的位置调整方向
        if (position === 'nw' || position === 'sw') deltaX = -deltaX;
        if (position === 'nw' || position === 'ne') deltaY = -deltaY;

        // 使用较大的变化量来保持宽高比
        const delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY * aspectRatio;

        // 计算满足最小高度要求的最小宽度
        const minWidthForHeight = MIN_PREVIEW_HEIGHT * aspectRatio;
        const effectiveMinWidth = Math.max(MIN_PREVIEW_WIDTH, minWidthForHeight);

        const newWidth = Math.max(effectiveMinWidth, Math.min(MAX_RESIZE_SIZE, startWidth + delta));

        imgWrapper.style.width = `${newWidth}px`;
        asset.previewWidth = newWidth;
      };

      const onMouseUp = () => {
        isResizing = false;
        container.setAttribute('draggable', 'true');
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        onSync?.();
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    handlesContainer.appendChild(handle);
  });

  // 选中边框 - 更简洁的黑色边框
  const selectionBorder = document.createElement('div');
  selectionBorder.style.cssText = `
        position: absolute;
        inset: 0;
        border: 2px solid #171717;
        border-radius: 12px;
        pointer-events: none;
    `;
  handlesContainer.insertBefore(selectionBorder, handlesContainer.firstChild);

  return handlesContainer;
};

/**
 * 创建图片素材元素（用于 contenteditable 编辑器）
 * @param {Object} asset - 素材对象
 * @param {Object} options - 配置选项
 * @param {Function} options.onRemove - 删除回调
 * @param {Function} options.onTagClick - 标签点击回调 (asset, element, event) => void
 * @param {Function} options.onSync - 同步内容回调
 * @param {Function} options.makeDraggable - 使元素可拖拽的函数
 * @returns {HTMLElement} 素材容器元素
 */
export const createMaterialElement = (asset, options = {}) => {
  const { onRemove, onTagClick, onSync, makeDraggable } = options;

  const container = document.createElement('span');
  container.contentEditable = 'false';
  container.setAttribute('data-type', 'image');
  container.setAttribute('data-asset-id', asset.id);
  container.setAttribute('data-url', asset.url);
  if (asset.tag) container.setAttribute('data-tag', asset.tag);
  container.className =
    'material-node inline-block relative my-1.5 rounded-xl overflow-visible select-none group cursor-grab draggable-item transition-all duration-200';

  if (makeDraggable) {
    makeDraggable(container);
  }

  // 图片包装器（用于尺寸调整）
  const imgWrapper = document.createElement('div');
  imgWrapper.className =
    'img-wrapper relative rounded-xl overflow-hidden border-2 border-transparent transition-all duration-200 ring-1 ring-black/[0.08] hover:ring-black/[0.15]';
  // 计算宽高比，确保最小尺寸时保持比例
  const aspectRatio = (asset.width || 1) / (asset.height || 1);
  // 高度 >= MIN_PREVIEW_HEIGHT 是第一优先级，计算满足此条件的最小宽度
  const minWidthForHeight = MIN_PREVIEW_HEIGHT * aspectRatio;
  // 取两者较大值，确保高度 >= MIN_PREVIEW_HEIGHT 且宽度 >= MIN_PREVIEW_WIDTH
  const minWidth = Math.max(MIN_PREVIEW_WIDTH, minWidthForHeight);
  // 初始预览尺寸，始终应用最小限制（优先保证高度）
  const baseWidth = asset.previewWidth || asset.width || DEFAULT_PREVIEW_SIZE;
  const previewWidth = Math.min(Math.max(minWidth, baseWidth), MAX_PREVIEW_SIZE);
  imgWrapper.style.width = `${previewWidth}px`;

  const img = document.createElement('img');
  img.src = asset.url;
  img.className = 'block w-full h-auto object-contain pointer-events-none';
  img.draggable = false;

  // 加载指示器覆盖层
  if (asset.loading) {
    const loaderOverlay = document.createElement('div');
    loaderOverlay.className =
      'loading-overlay absolute inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center rounded-xl';
    const loader = document.createElement('span');
    loader.className =
      'loading-spinner w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin';
    loaderOverlay.appendChild(loader);
    imgWrapper.appendChild(loaderOverlay);
  }

  // 删除按钮 - 右上角，更简洁的设计
  const removeBtn = document.createElement('button');
  removeBtn.className =
    'remove-btn absolute top-1.5 right-1.5 w-6 h-6 rounded-lg bg-black/70 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg backdrop-blur-sm z-10';
  removeBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>`;
  removeBtn.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const assetId = container.getAttribute('data-asset-id');
    container.remove();
    onRemove?.(assetId, asset.url);
    onSync?.();
  };

  // 创建 4 角 resize handles
  const resizeHandles = createResizeHandles(imgWrapper, container, asset, onSync);

  // 选中状态管理
  let isSelected = false;

  const setSelected = (selected) => {
    isSelected = selected;
    if (selected) {
      resizeHandles.style.opacity = '1';
      imgWrapper.classList.add('border-neutral-900');
      imgWrapper.classList.remove('border-transparent');
    } else {
      resizeHandles.style.opacity = '0';
      imgWrapper.classList.remove('border-neutral-900');
      imgWrapper.classList.add('border-transparent');
    }
  };

  // 点击选中
  imgWrapper.onclick = (e) => {
    e.stopPropagation();
    // 取消其他素材的选中状态
    document.querySelectorAll('[data-type="image"] .resize-handles').forEach((h) => {
      if (h !== resizeHandles) {
        h.style.opacity = '0';
        const wrapper = h.closest('.img-wrapper');
        if (wrapper) {
          wrapper.classList.remove('border-neutral-900');
          wrapper.classList.add('border-transparent');
        }
      }
    });
    setSelected(!isSelected);
  };

  // 点击外部取消选中
  const handleClickOutside = (e) => {
    if (!container.contains(e.target)) {
      setSelected(false);
    }
  };
  document.addEventListener('click', handleClickOutside);

  // 存储清理函数
  container._cleanupResize = () => {
    document.removeEventListener('click', handleClickOutside);
  };

  // 用途标签触发器 - 底部居中，简洁现代的设计
  const usageLabel = document.createElement('button');
  usageLabel.className = `usage-trigger absolute bottom-1.5 left-1.5 right-1.5 flex items-center justify-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-all duration-200 cursor-pointer z-10 ${
    asset.tag
      ? 'bg-neutral-900/90 text-white backdrop-blur-sm shadow-lg'
      : 'bg-black/50 text-white/90 backdrop-blur-sm opacity-0 group-hover:opacity-100'
  }`;

  // 标签图标
  const tagIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  tagIcon.setAttribute('width', '12');
  tagIcon.setAttribute('height', '12');
  tagIcon.setAttribute('viewBox', '0 0 24 24');
  tagIcon.setAttribute('fill', 'none');
  tagIcon.setAttribute('stroke', 'currentColor');
  tagIcon.setAttribute('stroke-width', '2');
  tagIcon.setAttribute('stroke-linecap', 'round');
  tagIcon.setAttribute('stroke-linejoin', 'round');
  tagIcon.innerHTML =
    '<path d="m12 2-3.4 3.4a1 1 0 0 0-.3.7V11l-5 5a1 1 0 0 0 0 1.4l4.3 4.3a1 1 0 0 0 1.4 0l5-5h4.9a1 1 0 0 0 .7-.3L22 12"/><circle cx="19" cy="5" r="3"/>';
  tagIcon.style.flexShrink = '0';

  const labelText = document.createElement('span');
  labelText.className = 'truncate';
  labelText.textContent = asset.tag || i18n.t('material.clickToSetUsage');

  usageLabel.appendChild(tagIcon);
  usageLabel.appendChild(labelText);

  // 点击触发器，通过回调打开外部 Vue Popover
  // 注意：必须从 DOM 节点获取最新的属性值，而不是从闭包中的 asset 对象获取
  // 因为上传完成后 asset-id 会被更新，但闭包中的 asset 对象不会被更新
  usageLabel.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onTagClick) {
      // 从 DOM 节点获取最新的属性值
      const currentId = container.getAttribute('data-asset-id');
      const currentUrl = container.getAttribute('data-url');
      const currentTag = container.getAttribute('data-tag') || '';
      onTagClick(
        {
          id: currentId,
          url: currentUrl,
          tag: currentTag,
        },
        container,
        e
      );
    }
  };

  // 清理函数 - 存储在元素上以便后续清理
  container._cleanup = () => {
    document.removeEventListener('click', handleClickOutside);
  };

  imgWrapper.appendChild(img);
  imgWrapper.appendChild(removeBtn);
  imgWrapper.appendChild(usageLabel);
  container.appendChild(imgWrapper);
  container.appendChild(resizeHandles); // resize handles 放在外层容器，避免被 imgWrapper overflow 裁切

  return container;
};

/**
 * 创建简单图片节点（无用途标签，用于快速插入）
 * @param {Object} asset - 素材对象
 * @param {Object} options - 配置选项
 * @param {Function} options.onRemove - 删除回调
 * @param {Function} options.onSync - 同步内容回调
 * @param {Function} options.makeDraggable - 使元素可拖拽的函数
 * @returns {HTMLElement} 素材容器元素
 */
export const createSimpleMaterialElement = (asset, options = {}) => {
  const { onRemove, onSync, makeDraggable } = options;

  const container = document.createElement('span');
  container.contentEditable = 'false';
  container.setAttribute('data-type', 'image');
  container.setAttribute('data-asset-id', asset.id);
  container.setAttribute('data-url', asset.url);
  container.className =
    'material-node inline-block relative my-1.5 rounded-xl overflow-visible select-none group cursor-grab draggable-item transition-all duration-200';

  if (makeDraggable) {
    makeDraggable(container);
  }

  // 图片包装器
  const imgWrapper = document.createElement('div');
  imgWrapper.className =
    'img-wrapper relative rounded-xl overflow-hidden border-2 border-transparent transition-all duration-200 ring-1 ring-black/[0.08] hover:ring-black/[0.15]';
  // 计算宽高比，确保最小尺寸时保持比例
  const aspectRatio = (asset.width || 1) / (asset.height || 1);
  // 高度 >= MIN_PREVIEW_HEIGHT 是第一优先级，计算满足此条件的最小宽度
  const minWidthForHeight = MIN_PREVIEW_HEIGHT * aspectRatio;
  // 取两者较大值，确保高度 >= MIN_PREVIEW_HEIGHT 且宽度 >= MIN_PREVIEW_WIDTH
  const minWidth = Math.max(MIN_PREVIEW_WIDTH, minWidthForHeight);
  // 初始预览尺寸，始终应用最小限制（优先保证高度）
  const baseWidth = asset.previewWidth || asset.width || DEFAULT_PREVIEW_SIZE;
  const previewWidth = Math.min(Math.max(minWidth, baseWidth), MAX_PREVIEW_SIZE);
  imgWrapper.style.width = `${previewWidth}px`;

  // 图片
  const img = document.createElement('img');
  img.src = asset.url;
  img.className = 'block w-full h-auto object-contain pointer-events-none';
  img.draggable = false;

  // 加载指示器覆盖层
  if (asset.loading) {
    const loaderOverlay = document.createElement('div');
    loaderOverlay.className =
      'loading-overlay absolute inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center rounded-xl';
    const loader = document.createElement('span');
    loader.className =
      'loading-spinner w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin';
    loaderOverlay.appendChild(loader);
    imgWrapper.appendChild(loaderOverlay);
  }

  // 删除按钮 - 右上角，更简洁的设计
  const removeBtn = document.createElement('button');
  removeBtn.className =
    'remove-btn absolute top-1.5 right-1.5 w-6 h-6 rounded-lg bg-black/70 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg backdrop-blur-sm z-10';
  removeBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>`;
  removeBtn.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const assetId = container.getAttribute('data-asset-id');
    container.remove();
    onRemove?.(assetId, asset.url);
    onSync?.();
  };

  // 创建 4 角 resize handles
  const resizeHandles = createResizeHandles(imgWrapper, container, asset, onSync);

  // 选中状态管理
  let isSelected = false;

  const setSelected = (selected) => {
    isSelected = selected;
    if (selected) {
      resizeHandles.style.opacity = '1';
      imgWrapper.classList.add('border-neutral-900');
      imgWrapper.classList.remove('border-transparent');
    } else {
      resizeHandles.style.opacity = '0';
      imgWrapper.classList.remove('border-neutral-900');
      imgWrapper.classList.add('border-transparent');
    }
  };

  // 点击选中
  imgWrapper.onclick = (e) => {
    e.stopPropagation();
    // 取消其他素材的选中状态
    document.querySelectorAll('[data-type="image"] .resize-handles').forEach((h) => {
      if (h !== resizeHandles) {
        h.style.opacity = '0';
        const wrapper = h.closest('.img-wrapper');
        if (wrapper) {
          wrapper.classList.remove('border-neutral-900');
          wrapper.classList.add('border-transparent');
        }
      }
    });
    setSelected(!isSelected);
  };

  // 点击外部取消选中
  const handleClickOutside = (e) => {
    if (!container.contains(e.target)) {
      setSelected(false);
    }
  };
  document.addEventListener('click', handleClickOutside);

  // 存储清理函数
  container._cleanupResize = () => {
    document.removeEventListener('click', handleClickOutside);
  };

  imgWrapper.appendChild(img);
  imgWrapper.appendChild(removeBtn);
  imgWrapper.appendChild(resizeHandles);
  container.appendChild(imgWrapper);

  return container;
};

/**
 * 更新图片节点（用于替换临时节点为真实素材）
 * @param {HTMLElement} editorRef - 编辑器元素
 * @param {string} tempId - 临时 ID
 * @param {Object} asset - 新的素材对象
 * @param {Function} onSync - 同步内容回调
 */
export const updateMaterialNode = (editorRef, tempId, asset, onSync) => {
  if (!editorRef) return;
  const node = editorRef.querySelector(`[data-asset-id="${tempId}"]`);
  if (node) {
    node.setAttribute('data-asset-id', asset.id);
    node.setAttribute('data-url', asset.url);
    if (asset.tag) {
      node.setAttribute('data-tag', asset.tag);
      updateUsageLabel(node, asset.tag);
    }
    // 更新图片 src
    const img = node.querySelector('img');
    if (img) {
      img.src = asset.url;
    }
    // 移除加载指示器覆盖层
    const loaderOverlay = node.querySelector('.loading-overlay');
    if (loaderOverlay) {
      loaderOverlay.remove();
    }
    onSync?.();
  }
};

/**
 * 更新素材节点的标签
 * @param {HTMLElement} editorRef - 编辑器元素
 * @param {string} assetId - 素材 ID
 * @param {string} tag - 新的标签
 * @param {Function} onSync - 同步内容回调
 */
export const updateMaterialTagById = (editorRef, assetId, tag, onSync) => {
  if (!editorRef) return;
  const node = editorRef.querySelector(`[data-asset-id="${assetId}"]`);
  if (node) {
    node.setAttribute('data-tag', tag || '');
    updateUsageLabel(node, tag);
    onSync?.();
  }
};

/**
 * 通过 ID 移除素材节点
 * @param {HTMLElement} editorRef - 编辑器元素
 * @param {string} assetId - 素材 ID
 * @param {Function} onSync - 同步内容回调
 */
export const removeMaterialNodeById = (editorRef, assetId, onSync) => {
  if (!editorRef) return;
  const node = editorRef.querySelector(`[data-asset-id="${assetId}"]`);
  if (node) {
    // 清理事件监听
    if (node._cleanup) {
      node._cleanup();
    }
    if (node._cleanupResize) {
      node._cleanupResize();
    }
    node.remove();
    onSync?.();
  }
};

/**
 * Get caret coordinates for positioning floating menus
 * @returns {Object|null} { x, y }
 */
export const getCaretCoordinates = () => {
  const selection = window.getSelection();
  if (selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  let rect = range.getBoundingClientRect();

  // If rect is empty (e.g. at start of empty line), try getClientRects
  if (rect.width === 0 && rect.height === 0) {
    const rects = range.getClientRects();
    if (rects.length > 0) {
      rect = rects[0];
    } else {
      // Fallback: use text node parent if possible or default to active element
      if (range.startContainer.nodeType === Node.ELEMENT_NODE) {
        const element = range.startContainer.childNodes[range.startOffset];
        if (element && element.getBoundingClientRect) {
          rect = element.getBoundingClientRect();
        }
      }
    }
  }

  // Return coordinates relative to viewport
  return {
    x: rect.left,
    y: rect.bottom,
  };
};
